import { memo, useMemo, useState } from 'react';
import { useTranslate } from '@tolgee/react';
import { Button, Input, Radio, Select, Tooltip, Spin } from 'antd';
import { EditOutlined, PlusOutlined, CloseOutlined } from '@ant-design/icons';
import { FieldArray } from 'formik';
import { useCreation } from 'ahooks';
import FormField from 'components/FormField';
import { css, cx } from '@emotion/css';
import { get, map } from 'lodash-es';
import { ItemFilter, Condition, ItemGroupSpu } from './type';
import useSmartField from 'hooks/useSmartField';
import useModal from 'hooks/useModal';
import {
  ItemSelectV2,
  ItemSelectV2ComponentProps,
} from '@knowledge/common/components/ItemSelectV2';
import { convertItemstoItemGroupSpus } from './helper';
import { getItemsByInternalIds } from '@knowledge/common/cache/spu';

interface ConditionFieldProps {
  index: number;
  prefix: string;
  conditions?: Condition[];
  reverseType?: boolean;
  givenType?: 'add' | 'delete';
  btnLoading?: boolean; // 指定商品时的按钮loading
  errors?: any;
  trySubmit?: () => void;
  storeScopes: General.StoreScope[];
}

const defaultConditions: Condition[] = ['START_WITH', 'CONTAINS', 'END_WITH', 'IN_LIST'];
const conditionLabelMap: Record<Condition, string> = {
  START_WITH: '前缀为',
  END_WITH: '后缀为',
  CONTAINS: '包含',
  IN_LIST: '属于',
};

/**
 * 交互上需要在以下3种情况自动提交搜索
 * 1、当type是sku的时候，任意输入框失去焦点，且有值的时候
 * 2、当type是sku的时候，删除任意有值的input
 * 3、当type是given的时候，点击添加商品或删除商品
 */
const ConditionField = memo((props: ConditionFieldProps) => {
  const { t } = useTranslate();
  const {
    index,
    prefix,
    reverseType = false,
    conditions = defaultConditions,
    givenType = 'add',
    btnLoading = false,
    trySubmit,
    storeScopes,
  } = props;
  const types = useMemo(() => {
    return reverseType ? ['PLATFORM_SKU', 'SELLER_SKU'] : ['SELLER_SKU', 'PLATFORM_SKU'];
  }, [reverseType]);
  const typeLabelMap = useCreation<Record<string, string>>(() => {
    return {
      SELLER_SKU: t('通过SKU货号匹配商品'),
      PLATFORM_SKU: t('指定平台商品'),
    };
  }, [t]);
  const prefixKey = `${prefix}.${index}`;
  const { field } = useSmartField<ItemFilter>(prefixKey);
  const { show, hide, meta, visible } =
    useModal<Pick<ItemSelectV2ComponentProps, 'value' | 'onChange' | 'storeScopes'>>();
  const [loading, setLoading] = useState(false);

  return (
    <div
      className={css`
        .ant-form-item {
          margin-bottom: 0;
        }
      `}
    >
      {visible && meta && (
        <ItemSelectV2 {...meta} visible={visible} onCancel={() => hide()} storeType="ALL" />
      )}
      <div className="my-[9px]">
        <FormField name={`${prefixKey}.target`}>
          {({ field }) => {
            const { onChange, ...rest } = field;
            return (
              <Radio.Group
                onChange={(e) => {
                  onChange(e.target.value);
                  trySubmit?.();
                }}
                {...rest}
              >
                {types.map((type) => {
                  return <Radio value={type}>{typeLabelMap[type]}</Radio>;
                })}
              </Radio.Group>
            );
          }}
        </FormField>
      </div>
      {field.value.target === 'SELLER_SKU' ? (
        <FieldArray name={`${prefixKey}.itemFilterPair`} shouldUpdate={() => true}>
          {({ push, remove }) => {
            return (
              <div
                className={cx({
                  'pl-[36px]': field.value.itemFilterPair.length > 1,
                  relative: field.value.itemFilterPair.length > 1,
                })}
              >
                {field.value.itemFilterPair.length > 1 && (
                  <div className="absolute left-[0px] w-[23px] flex flex-col items-center top-[26px]">
                    {Array.from({ length: field.value.itemFilterPair.length - 1 }).map(
                      (_, index) => {
                        return (
                          <div
                            key={index}
                            className="border border-solid border-[#ddd] px-[4px] h-[24px] flex items-center min-w-[24px] mt-[20px] first:mt-0 relative"
                          >
                            <div className="w-[1px] h-[11px] absolute bg-[#ddd] left-[50%] top-[-11px]"></div>
                            {t('且')}
                            <div className="w-[1px] h-[11px] absolute bg-[#ddd] left-[50%] bottom-[-11px]"></div>
                          </div>
                        );
                      },
                    )}
                  </div>
                )}
                <div>
                  {field.value.itemFilterPair.map((itemFilter, index) => {
                    return (
                      <div
                        className={cx('flex items-center mb-[12px] last:mb-0', {
                          relative: field.value.itemFilterPair.length > 1,
                        })}
                        key={index}
                      >
                        <div className="absolute w-[24px] h-[1px] bg-[#ddd] top-[16px] left-[-24px]"></div>
                        <FormField
                          name={`${prefixKey}.itemFilterPair.${index}.condition`}
                          className="!mr-[8px]"
                        >
                          {({ field }) => {
                            const { onChange, ...rest } = field;
                            return (
                              <Select
                                dropdownMatchSelectWidth={false}
                                className="min-w-[120px]"
                                onChange={(value) => {
                                  field.onChange(value);
                                  if (!!itemFilter.keyword) {
                                    setTimeout(() => {
                                      trySubmit?.();
                                    });
                                  }
                                }}
                                {...rest}
                              >
                                {conditions.map((item) => (
                                  <Select.Option key={item} value={item}>
                                    {t(conditionLabelMap[item])}
                                  </Select.Option>
                                ))}
                              </Select>
                            );
                          }}
                        </FormField>
                        <FormField
                          name={`${prefixKey}.itemFilterPair.${index}.keyword`}
                          className={cx(
                            'flex-grow',
                            css`
                              .ant-form-item-explain-error {
                                height: 0 !important;
                                overflow: hidden;
                              }
                              .ant-form-item-explain.ant-form-item-explain-connected {
                                min-height: 0;
                              }
                            `,
                          )}
                        >
                          {({ field }) => {
                            const { onBlur, ...rest } = field;
                            return (
                              <Input
                                showCount
                                maxLength={itemFilter.condition === 'IN_LIST' ? undefined : 20}
                                placeholder={
                                  itemFilter.condition === 'IN_LIST'
                                    ? t('多个使用逗号分隔')
                                    : t('请输入')
                                }
                                {...rest}
                                onBlur={() => {
                                  trySubmit?.();
                                }}
                              />
                            );
                          }}
                        </FormField>
                        {index > 0 ? (
                          <Tooltip title={t('marketing.删除')}>
                            <Button
                              type="text"
                              icon={
                                <CloseOutlined
                                  className="flex-shrink-0 cursor-pointer"
                                  onClick={() => {
                                    const currentValue = get(
                                      field.value,
                                      `itemFilterPair.${index}.keyword`,
                                    );
                                    remove(index);
                                    if (!!currentValue) {
                                      trySubmit?.();
                                    }
                                  }}
                                />
                              }
                            />
                          </Tooltip>
                        ) : (
                          <Tooltip title={t('marketing.新增')}>
                            <Button
                              type="text"
                              disabled={field.value.itemFilterPair.length >= 3}
                              icon={
                                <PlusOutlined
                                  className="flex-shrink-0 cursor-pointer"
                                  onClick={() => {
                                    if (field.value.itemFilterPair.length < 3) {
                                      push({
                                        condition: conditions[0],
                                        keyword: '',
                                      });
                                    }
                                  }}
                                />
                              }
                            />
                          </Tooltip>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          }}
        </FieldArray>
      ) : (
        <FormField<ItemGroupSpu[]> name={`${prefixKey}.specifiedItem`} noStyle>
          {({ field }) => {
            if (get(field.value, 'length', 0) === 0) {
              return (
                <Button
                  block
                  loading={btnLoading}
                  onClick={() => {
                    show({
                      value: [],
                      onChange: (value) => {
                        const spus = convertItemstoItemGroupSpus(value);
                        field.onChange(spus);
                        setTimeout(() => {
                          trySubmit?.();
                        });
                      },
                      storeScopes,
                    });
                  }}
                >
                  {givenType === 'add' ? `+ ${t('aide.添加商品')}` : `- ${t('删除商品')}`}
                </Button>
              );
            }
            return (
              <div className="h-[32px] flex items-center px-[8px] bg-[var(--fett-erp-card-bg)] gap-[8px]">
                {t('knowledge.x 个商品', { x: field.value.length })}
                {loading ? (
                  <Spin />
                ) : (
                  <EditOutlined
                    className="cursor-pointer"
                    onClick={() => {
                      setLoading(true);
                      getItemsByInternalIds(
                        map(field.value, (each) => {
                          return {
                            itemInternalId: each.itemInternalId,
                            skus: each.sku,
                          };
                        }),
                      )
                        .then((data) => {
                          console.log(data);
                          show({
                            value: data,
                            onChange: (value) => {
                              const spus = convertItemstoItemGroupSpus(value);
                              field.onChange(spus);
                              trySubmit?.();
                            },
                            storeScopes,
                          });
                        })
                        .finally(() => {
                          setLoading(false);
                        });
                    }}
                  />
                )}
              </div>
            );
          }}
        </FormField>
      )}
    </div>
  );
});

ConditionField.displayName = 'ItemGroupConditionField';

export default ConditionField;
